import { UIButton, bindUIFocus } from '../ui/UIButton';
import { UITheme } from '../ui/UITheme';
import * as Phaser from 'phaser';
import { LocalizationManager } from '../managers/LocalizationManager';
import { VolumeSlider } from '../ui/VolumeSlider';
import { AudioManager } from '../managers/AudioManager';
import { getSharedProfileRuntime, ProfileRuntime } from '../managers/ProfileRuntime';
import { createLogger } from '../utils/Logger';
import { createVersionLabel } from '../ui/VersionLabel';
import { createTelegramLink } from '../ui/TelegramLink';

export class MainMenuScene extends Phaser.Scene {
    private bgRect!: Phaser.GameObjects.Rectangle;
    private environment?: Phaser.GameObjects.Image;
    private illustration?: Phaser.GameObjects.Image;
    private titleText!: Phaser.GameObjects.Text;
    private subtitleText!: Phaser.GameObjects.Text;
    private recordsText?: Phaser.GameObjects.Text;
    private playButton!: UIButton;
    private languageButton!: UIButton;
    private muteButton!: UIButton;
    private settingsButton!: UIButton;
    private settingsPanel!: Phaser.GameObjects.Container;
    private settingsBackdrop!: Phaser.GameObjects.Rectangle;
    private settingsClose!: UIButton;
    private settingsFocus?: () => void;
    private tooltip!: Phaser.GameObjects.Text;
    private effectButtons: UIButton[] = [];
    private volumeSlider!: VolumeSlider;
    private versionLabel!: Phaser.GameObjects.Text | null;
    private telegramLink!: Phaser.GameObjects.Text;

    private profileRuntime!: ProfileRuntime;
    private audioManager!: AudioManager;
    private readonly logger = createLogger('MainMenuScene');

    constructor() {
        super('MainMenu');
    }

    create() {
        const width = this.scale.width;
        const height = this.scale.height;
        this.profileRuntime = getSharedProfileRuntime();
        this.profileRuntime.loadProfile();
        this.audioManager = new AudioManager(this, this.profileRuntime);
        this.audioManager.setMissingAudioWarningsSuppressed(new URLSearchParams(window.location.search).has('e2e'));
        const loc = LocalizationManager.getInstance();

        // Background
        this.bgRect = this.add.rectangle(0, 0, width, height, UITheme.background).setOrigin(0);
        this.environment = this.textures.exists('menu_environment')
            ? this.add.image(0, 0, 'menu_environment').setOrigin(0.5)
            : undefined;
        this.illustration = this.textures.exists('menu_emblem')
            ? this.add.image(0, 0, 'menu_emblem').setOrigin(0.5)
            : undefined;

        // Title
        this.titleText = this.add.text(width / 2, height * 0.20, loc.getText('menu.title'), {
            fontFamily: UITheme.heading,
            fontSize: '64px',
            color: UITheme.text,
            fontStyle: 'bold',
            stroke: '#101f1c',
            strokeThickness: 2
        }).setOrigin(0.5);

        // Subtitle
        this.subtitleText = this.add.text(width / 2, height * 0.32, loc.getText('menu.subtitle'), {
            fontFamily: UITheme.body,
            fontSize: '20px',
            color: UITheme.muted
        }).setOrigin(0.5);

        this.playButton = new UIButton(this, width / 2, height * 0.64, loc.getText('menu.play'), () => this.startGame(), 300, 56);
        this.languageButton = new UIButton(this, width - 70, 30, loc.getLanguage().toUpperCase(), () => {
            loc.setLanguage(loc.getLanguage() === 'ru' ? 'en' : 'ru');
            document.documentElement.lang = loc.getLanguage();
            this.scene.restart();
        }, 52, 48, true);
        document.documentElement.lang = loc.getLanguage();

        this.tooltip = this.add.text(0, 0, '', { fontFamily: UITheme.body, fontSize: '14px', color: UITheme.text, backgroundColor: '#101f1c', padding: {x: 8, y: 4} }).setOrigin(0.5).setVisible(false).setDepth(20);
        this.muteButton = new UIButton(this, 0, 0, '', () => {
            this.audioManager.setMute(!this.audioManager.getMute());
            this.muteButton.setAlpha(this.audioManager.getMute() ? 0.6 : 1);
            this.showTooltip(this.getMuteString(loc, this.audioManager.getMute()));
        }, 52, 48, true);
        this.settingsButton = new UIButton(this, 0, 0, '', () => this.toggleSettings(true), 52, 48, true);
        for (const [button, key, fallback] of [[this.muteButton, 'ui_sound', 'S'], [this.settingsButton, 'ui_equipment', '…']] as const) {
            if (this.textures.exists(key)) button.add(this.add.image(0, 0, key).setDisplaySize(26, 26));
            else button.setText(fallback);
        }
        this.muteButton.setAlpha(this.audioManager.getMute() ? 0.6 : 1);
        this.createSettings(loc);
        this.createProfileRecords(width, height, loc);
        this.versionLabel = createVersionLabel(this);
        this.telegramLink = createTelegramLink(this).setText(loc.getText('menu.community'));
        const controls = [this.muteButton, this.settingsButton, this.languageButton];
        const labels = [() => this.getMuteString(loc, this.audioManager.getMute()), () => loc.getText('menu.settings'), () => loc.getText('menu.language')];
        controls.forEach((button, i) => {
            button.on('pointerover', () => this.showTooltip(labels[i]!()));
            button.on('pointerout', () => this.tooltip.setVisible(false));
        });
        bindUIFocus(this, [this.playButton, ...controls.map((button, i) => ({
            setFocused: (focused: boolean) => { button.setFocused(focused); if (focused) this.showTooltip(labels[i]!()); else this.tooltip.setVisible(false); },
            activate: () => button.activate()
        })), {
            setFocused: focused => this.telegramLink.setStyle({ color: focused ? '#ecc778' : UITheme.text }),
            activate: () => window.open('https://t.me/+zDjN3qxWP6EyY2Q6', '_blank', 'noopener,noreferrer')
        }]);
        const escape = () => { if (this.settingsPanel.visible) this.toggleSettings(false); };
        this.input.keyboard?.on('keydown-ESC', escape);
        this.events.once('shutdown', () => { this.settingsFocus?.(); this.settingsFocus = undefined; this.input.keyboard?.off('keydown-ESC', escape); });
        // Resize handler
        this.scale.on('resize', this.resizeLayout, this);
        this.events.once('shutdown', () => {
            this.scale.off('resize', this.resizeLayout, this);
        });

        // Trigger initial resize layout positioning
        this.resizeLayout();

        // Start playing menu music
        if (!this.sound.get('menu_theme') || !this.sound.get('menu_theme').isPlaying) {
            this.sound.stopAll();
            this.audioManager.playMusic('menu_theme', { loop: true, volume: 1 });
        }
    }

    private resizeLayout() {
        const {width, height} = this.scale;
        const portrait = width < height;
        const compact = height < 500;
        const artWidth = portrait ? width : width * 0.46;
        const artHeight = portrait ? Math.min(390, height * 0.42) : height - 32;
        const emblemSize = Math.min(artWidth - 24, artHeight - 24);
        this.bgRect.setSize(width, height);
        if (this.environment) {
            const source = this.environment.texture.getSourceImage();
            this.environment.setScale(Math.max(width / source.width, height / source.height)).setPosition(width / 2, height / 2);
        }
        this.illustration?.setDisplaySize(emblemSize, emblemSize).setPosition(artWidth / 2, portrait ? artHeight / 2 : height / 2);
        const centerX = portrait ? width / 2 : width * 0.73;
        const availableWidth = portrait ? width - 32 : width * 0.50;
        const blockHeight = compact ? 232 : 300;
        const top = portrait ? artHeight + Math.max(30, (height - artHeight - blockHeight - 56) / 2) : Math.max(36, (height - blockHeight) / 2);
        this.titleText.setPosition(centerX, top).setScale(1).setFontSize(compact ? 30 : 48);
        this.titleText.setScale(Math.min(1, availableWidth / this.titleText.width));
        this.subtitleText.setPosition(centerX, top + 42).setFontSize(16).setVisible(!compact);
        const playY = top + (compact ? 62 : 100);
        this.playButton.setPosition(centerX, playY).setScale(1);
        this.recordsText?.setPosition(centerX, playY + 50).setFontSize(compact ? 14 : 16);
        const rowY = playY + 108;
        this.muteButton.setPosition(centerX - 64, rowY);
        this.settingsButton.setPosition(centerX, rowY);
        this.languageButton.setPosition(centerX + 64, rowY);
        this.tooltip.setPosition(centerX, rowY + 42).setVisible(false);
        this.telegramLink.setPosition(portrait ? width / 2 : centerX, height - 28);
        this.versionLabel?.setPosition(width - 12, height - 12);
        this.settingsPanel.setPosition(width / 2, height / 2);
        this.settingsBackdrop.setSize(width, height);
        this.logger.debug('resizeLayout', {width, height, portrait, compact});
    }

    private showTooltip(text: string): void {
        this.tooltip.setText(text).setVisible(!this.settingsPanel.visible);
    }

    private createSettings(loc: LocalizationManager): void {
        this.settingsBackdrop = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x07130f, 0.94).setInteractive();
        const panel = this.add.rectangle(0, 0, 304, 316, UITheme.background).setStrokeStyle(1, UITheme.hover);
        const title = this.add.text(0, -128, loc.getText('menu.settings'), {fontFamily: UITheme.body, fontSize: '24px', color: UITheme.text, fontStyle: 'bold'}).setOrigin(0.5);
        this.volumeSlider = new VolumeSlider(this, 0, -56, 200, this.audioManager.getMusicVolume(), value => {
            this.audioManager.setMusicVolume(value);
            (this.sound.get('menu_theme') as Phaser.Sound.WebAudioSound | undefined)?.setVolume(value);
        });
        this.effectButtons = ['reduceShake', 'reduceFlashes'].map((key, i) => {
            let enabled = this.profileRuntime.loadProfile().settings.gameplay[key] === true;
            const label = () => loc.getText('ui.' + key) + ': ' + loc.getText(enabled ? 'ui.enabled' : 'ui.disabled');
            const button = new UIButton(this, 0, i * 54, label(), () => {
                const result = this.profileRuntime.updateSettings({gameplay: {[key]: !enabled}});
                enabled = result.profile.settings.gameplay[key] === true;
                button.setText(label());
            }, 272, 46, true);
            button.label.setFontSize(16);
            return button;
        });
        this.settingsClose = new UIButton(this, 0, 118, loc.getText('menu.close'), () => this.toggleSettings(false), 160, 44, true);
        this.settingsPanel = this.add.container(0, 0, [this.settingsBackdrop, panel, title, this.volumeSlider, ...this.effectButtons, this.settingsClose]).setDepth(1000).setVisible(false);
    }

    private toggleSettings(visible: boolean): void {
        this.settingsPanel.setVisible(visible);
        this.tooltip.setVisible(false);
        this.settingsFocus?.();
        this.settingsFocus = visible ? bindUIFocus(this, [this.volumeSlider, ...this.effectButtons, this.settingsClose]) : undefined;
        this.logger.debug('settings:visibility', {visible});
    }

    private createProfileRecords(width: number, height: number, loc: LocalizationManager): void {
        const snapshot = this.profileRuntime.getProfileSnapshot();
        if (!snapshot.ok) {
            this.logger.warn('createProfileRecords:profile-unavailable', { error: snapshot.error });
            return;
        }
        const records = snapshot.profile.records;
        this.recordsText = this.add.text(width / 2, height * 0.49, [
            loc.getText('profile.records.best_time', [Math.floor(records.bestSurvivedSeconds)])
        ], {
            fontFamily: UITheme.body,
            fontSize: '20px',
            color: UITheme.muted,
            align: 'center'
        }).setOrigin(0.5);
        this.logger.debug('createProfileRecords:rendered', { totalRuns: records.totalRuns });
    }

    private getMuteString(loc: LocalizationManager, isMuted: boolean): string {
        return isMuted ? loc.getText('ui.sound.mute_on') : loc.getText('ui.sound.mute_off');
    }

    private startGame() {
        this.sound.stopAll();
        // The click interaction guarantees audio context is active for the next scene
        this.scene.start('Main');
    }
}
